(() => {
  if (customElements.get('yano-catalog')) return;

  class YanoCatalog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.dialog.id = `${this.id}-dialog`;
      this.querySelectorAll('[data-product]').forEach(link => {
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', this.dialog.id);
      });
      this.body = this.querySelector('.yano-dialog-body');
      this.money = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: this.dataset.currency || 'JPY' });
      this.querySelector('.yano-filters').hidden = false;
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', event => {
        if (event.key === 'Tab') this.querySelectorAll('.yano-pointer-open').forEach(link => link.classList.remove('yano-pointer-open'));
      });
      this.dialog.addEventListener('change', this.update.bind(this));
      this.dialog.addEventListener('input', this.update.bind(this));
      this.dialog.addEventListener('submit', this.addToCart.bind(this));
      this.dialog.addEventListener('cancel', event => {
        if (this.busy) event.preventDefault();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('yano-modal-open');
        this.opener?.focus({ preventScroll: true });
      });
      this.dialog.addEventListener('pointerdown', event => { this.backdropStart = event.target === this.dialog; });
    }

    format(cents) { return this.money.format(cents / 100); }

    onClick(event) {
      const filter = event.target.closest('[data-category]');
      if (filter) {
        this.querySelectorAll('[data-category]').forEach(button => button.setAttribute('aria-pressed', String(button === filter)));
        let count = 0;
        this.querySelectorAll('.yano-card').forEach(card => {
          card.hidden = !!filter.dataset.category && card.dataset.type !== filter.dataset.category;
          if (!card.hidden) count++;
        });
        this.querySelector('[data-count]').textContent = count;
      }
      const opener = event.target.closest('[data-product]');
      if (opener && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        event.preventDefault();
        this.opener = opener;
        opener.classList.toggle('yano-pointer-open', event.detail > 0);
        this.body.replaceChildren(this.querySelector(`[data-detail="${opener.dataset.product}"]`).content.cloneNode(true));
        this.variants = JSON.parse(this.body.querySelector('[data-variants]').textContent);
        this.form = this.body.querySelector('form');
        this.bundle = this.form.dataset.bundle === 'true';
        this.multiple = !!this.form.querySelector('.yano-options');
        this.quantities = new Map();
        this.unit = this.form.dataset.unit || '個';
        this.quantity = this.form.elements.quantity;
        this.button = this.form.querySelector('[type="submit"]');
        this.error = this.form.querySelector('.yano-error');
        this.update();
        document.querySelector('cart-notification')?.close();
        this.dialog.showModal();
        document.documentElement.classList.add('yano-modal-open');
        this.dialog.scrollTop = 0;
        this.querySelector('.yano-close').focus({ preventScroll: true });
      }
      const image = event.target.closest('[data-image-url]');
      if (image) {
        const main = this.body.querySelector('.yano-main-image img');
        main.removeAttribute('srcset');
        main.src = image.dataset.imageUrl;
        main.alt = image.dataset.imageAlt;
        this.body.querySelectorAll('[data-image-url]').forEach(button => button.setAttribute('aria-pressed', String(button === image)));
      }
      const stepper = event.target.closest('[data-step]');
      if (stepper && !this.busy) {
        if (!this.selectedVariant()) {
          this.showError(this.bundle ? '黒と白、両方のTシャツサイズを選択してください。' : 'サイズを選択してください。');
          return;
        }
        const step = Number(this.quantity.step) || 1;
        const direction = Number(stepper.dataset.step);
        const current = Math.max(0, Number(this.quantity.value) || 0);
        const min = this.selectedVariant()?.min || 1;
        this.quantity.value = direction > 0 && current === 0 ? min : Math.max(0, current + direction * step);
        if (this.quantity.max) this.quantity.value = Math.min(Number(this.quantity.max), Number(this.quantity.value));
        this.update();
      }
      if ((event.target.closest('.yano-close') || (event.target === this.dialog && this.backdropStart)) && !this.busy) this.dialog.close();
    }

    selectedVariant() {
      if (this.bundle) {
        const selected = Array.from(this.form.querySelectorAll('[data-bundle-option]:checked'));
        if (selected.length !== 2) return undefined;
        return this.variants.find(variant => selected.every(input => variant.options?.[Number(input.dataset.bundleOption)] === input.value));
      }
      const id = new FormData(this.form).get('id');
      return this.variants.find(variant => String(variant.id) === id);
    }

    variantLabel(variant) {
      return this.bundle ? `黒：${variant.options[0]} / 白：${variant.options[1]}` : variant.title;
    }

    selection() {
      if (!this.multiple) return [{ variant: this.selectedVariant(), quantity: Number(this.quantity.value) }];
      const current = this.selectedVariant();
      return this.variants
        .map(variant => ({
          variant,
          quantity: this.quantities.get(String(variant.id)) ??
            (this.bundle && current?.id === variant.id ? Number(this.quantity.value) : 0)
        }))
        .filter(item => item.quantity !== 0);
    }

    update(event) {
      if (!this.form || this.busy) return;
      const variant = this.selectedVariant();
      if (this.bundle) this.form.elements.id.value = variant?.id || '';
      if (this.multiple && variant) {
        const id = String(variant.id);
        if (event?.target?.name === 'id' || event?.target?.name?.startsWith('bundle-option-')) {
          this.quantity.value = this.quantities.get(id) ?? (this.bundle ? variant.min || 1 : 0);
        }
        else this.quantities.set(id, Number(this.quantity.value));
      }
      const quantity = Number(this.quantity.value);
      this.quantity.step = variant?.step || 1;
      if (variant?.max != null) this.quantity.max = variant.max;
      else this.quantity.removeAttribute('max');
      if (variant) this.body.querySelector('[data-price]').textContent = this.format(variant.price);
      const selection = this.selection();
      const total = selection.reduce((sum, item) => sum + (item.variant?.price || 0) * Math.max(0, Number.isFinite(item.quantity) ? item.quantity : 0), 0);
      this.body.querySelector('[data-total]').textContent = this.format(total);
      this.form.querySelector('[data-selected-size]').textContent = this.multiple && variant ? ` / ${this.variantLabel(variant)}` : '';
      this.quantity.disabled = this.multiple && !variant;
      if (this.multiple) {
        const summary = this.form.querySelector('[data-size-summary]');
        const rows = selection.filter(item => item.quantity > 0).map(item => {
          const row = document.createElement('div');
          const size = document.createElement('dt');
          const count = document.createElement('dd');
          size.textContent = this.variantLabel(item.variant);
          count.textContent = `${item.quantity}${this.unit}`;
          row.append(size, count);
          return row;
        });
        summary.replaceChildren(...rows);
        summary.parentElement.hidden = rows.length === 0;
      }
      const available = variant ? variant.available : this.variants.some(item => item.available);
      this.button.disabled = !available;
      this.button.textContent = available ? 'カートに追加する' : '売り切れ';
      this.form.querySelector('[data-step="-1"]').disabled = !variant || quantity <= 0;
      this.form.querySelector('[data-step="1"]').disabled = !variant || (!!this.quantity.max && quantity >= Number(this.quantity.max));
      this.error.hidden = true;
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    confirmSizes(selection) {
      return new Promise(resolve => {
        const confirmation = document.createElement('dialog');
        confirmation.className = 'yano-dialog yano-size-confirmation';
        confirmation.innerHTML = `
          <h2>複数のサイズが選択されています</h2>
          <p>サイズ・枚数にお間違いありませんか？</p>
          <dl></dl>
          <form method="dialog">
            <button value="cancel" class="yano-confirm-back" autofocus>戻って修正</button>
            <button value="confirm" class="button button--primary yano-confirm-submit">この内容で追加</button>
          </form>`;
        const title = confirmation.querySelector('h2');
        title.id = `${this.id}-size-confirm-title`;
        confirmation.setAttribute('aria-labelledby', title.id);
        const list = confirmation.querySelector('dl');
        selection.forEach(item => {
          const row = document.createElement('div');
          const size = document.createElement('dt');
          const quantity = document.createElement('dd');
          size.textContent = item.variant.title;
          quantity.textContent = `${item.quantity}枚`;
          row.append(size, quantity);
          list.append(row);
        });
        confirmation.addEventListener('close', () => {
          const approved = confirmation.returnValue === 'confirm';
          confirmation.remove();
          this.button.focus({ preventScroll: true });
          resolve(approved);
        }, { once: true });
        this.append(confirmation);
        confirmation.showModal();
      });
    }

    renderAddedItems(cart, result) {
      const items = result.items || [];
      // Dawn renders one key by default; retain its notification, but include every size in a batch.
      let rows;
      if (items.length > 1 && result.sections?.['cart-notification-product']) {
        const parsed = new DOMParser().parseFromString(result.sections['cart-notification-product'], 'text/html');
        rows = items.map(item => {
          const source = parsed.getElementById(`cart-notification-product-${item.key}`);
          if (!source) throw new Error('Missing added item');
          const row = document.createElement('div');
          row.className = 'yano-added-item';
          row.append(...Array.from(source.childNodes));
          const detail = row.lastElementChild;
          if (detail) {
            const quantity = document.createElement('p');
            quantity.className = 'yano-added-quantity';
            quantity.textContent = `${item.quantity}${this.unit}`;
            detail.append(quantity);
          }
          return row;
        });
      }
      cart.setActiveElement(this.opener);
      cart.renderContents({ ...result, key: items[0]?.key || result.key });
      if (rows) document.getElementById('cart-notification-product').replaceChildren(...rows);
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.busy || this.confirming) return;
      const variant = this.selectedVariant();
      if (this.bundle && !variant) {
        this.showError('黒と白、両方のTシャツサイズを選択してください。');
        return;
      }
      if (this.multiple && variant) this.quantities.set(String(variant.id), Number(this.quantity.value));
      const selection = this.selection();
      if (!variant && !selection.some(item => item.variant)) { this.showError('サイズを選択してください。'); return; }
      if (!selection.length) { this.showError(`数量を1${this.unit || '個'}以上入力してください。`); return; }
      for (const item of selection) {
        const { variant: selected, quantity } = item;
        if (!selected?.available) { this.showError('この種類は売り切れです。'); return; }
        if (!Number.isSafeInteger(quantity) || quantity < selected.min || (quantity - selected.min) % selected.step !== 0 || (selected.max != null && quantity > selected.max)) {
          this.showError(`${this.multiple ? `${selected.title}: ` : ''}数量を${selected.min}${this.unit || '個'}以上${selected.max != null ? `・${selected.max}${this.unit || '個'}以下` : ''}で入力してください。`);
          this.quantity.focus();
          return;
        }
      }
      if (this.multiple && this.unit === '枚' && selection.length > 1) {
        this.confirming = true;
        try {
          if (!await this.confirmSizes(selection)) return;
        } catch {
          this.showError('確認画面を開けませんでした。ページを再読み込みしてお試しください。');
          return;
        } finally {
          this.confirming = false;
        }
      }
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      this.button.textContent = '追加しています…';
      this.error.hidden = true;
      const controls = Array.from(this.form.querySelectorAll('input, button, fieldset')).map(control => ({ control, disabled: control.disabled }));
      controls.forEach(({ control }) => { control.disabled = true; });
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sections = cart?.getSectionsToRender().map(section => section.id) || ['cart-icon-bubble'];
      let added = false;
      try {
        const response = await fetch(this.dataset.addUrl + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          signal: AbortSignal.timeout(30000),
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: selection.map(item => ({ id: item.variant.id, quantity: item.quantity })), sections, sections_url: window.location.pathname })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          this.showError(typeof result.description === 'string' ? result.description : '追加できませんでした。在庫と数量をご確認ください。');
          return;
        }
        added = true;
        this.dialog.close();
        this.quantity.value = 0;
        this.quantities?.clear();
        // Close the native dialog before opening the theme notification in the page layer.
        try {
          if (!cart || sections.some(id => !result.sections?.[id])) throw new Error('Missing cart sections');
          this.renderAddedItems(cart, result);
        } catch {
          const bubble = result.sections?.['cart-icon-bubble'];
          if (bubble) {
            const content = new DOMParser().parseFromString(bubble, 'text/html').querySelector('.shopify-section');
            if (content) document.getElementById('cart-icon-bubble').innerHTML = content.innerHTML;
          }
          const feedback = this.querySelector('.yano-feedback');
          feedback.replaceChildren(document.createTextNode('カートにアイテムが追加されました。 '));
          const link = document.createElement('a');
          link.href = this.dataset.cartUrl;
          link.textContent = 'カートを見る';
          feedback.append(link);
          feedback.hidden = false;
          feedback.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      } catch {
        if (!added) this.showError('通信結果を確認できませんでした。重複を避けるため、カートをご確認のうえ再度お試しください。');
      } finally {
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        controls.forEach(({ control, disabled }) => { control.disabled = disabled; });
        this.button.disabled = !selection.some(item => item.variant.available);
        this.button.textContent = 'カートに追加する';
      }
    }
  }
  customElements.define('yano-catalog', YanoCatalog);
})();
